import React, { useState, useCallback } from 'react'
import { Classes, Dialog } from "@blueprintjs/core"
import { observer } from "mobx-react"
import { PlanrButton } from 'modules/common/components/planr/button/Button'
import { Buttons } from "modules/common/components/form"
import { FileLink } from "modules/common/components/files/FileLink"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { PendingSignatureItemType } from 'modules/main/models/order-content-task-signatures'
import { AttachSignatureForm, AttachSignatureFormState } from './AttachSignatureForm'
import { eat } from "modules/common/services/typescript"
import styles from "./CryptoDialog.module.scss"

interface AttachSignatureDialogProps {
  isOpen: boolean
  selectedSignature: PendingSignatureItemType | null
  selectedSignerName: string | null
  onClose: () => void
  onAttachSignature: (signature: PendingSignatureItemType, signatureData: string) => Promise<void>
  onNotifyError: (error: string) => void
  onNotifySuccess?: (message: string) => void
  baseUrl: string
}

export const AttachSignatureDialog: React.FC<AttachSignatureDialogProps> = observer(({
  isOpen,
  selectedSignature,
  selectedSignerName,
  onClose,
  onAttachSignature,
  onNotifyError,
  onNotifySuccess,
  baseUrl,
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [form, setForm] = useState<AttachSignatureFormState>({ sigBase64: null, hashMatch: false, signerNames: [] })
  // Смена ключа размонтирует форму — так сбрасываются выбранные файлы
  const [formKey, setFormKey] = useState(0)

  const handleFormChange = useCallback((state: AttachSignatureFormState) => setForm(state), [])

  const resetForm = () => {
    setForm({ sigBase64: null, hashMatch: false, signerNames: [] })
    setFormKey((key) => key + 1)
  }

  const handleAttach = async () => {
    if (!selectedSignature || !form.sigBase64) {
      onNotifyError('Необходимо выбрать файл подписи (.sig)')
      return
    }

    if (!form.hashMatch) {
      onNotifyError('Сначала проверьте хэш файла результата. Он должен совпадать с файлом в базе.')
      return
    }

    try {
      setIsUploading(true)

      await onAttachSignature(selectedSignature, form.sigBase64)
      onClose()
      resetForm()
      onNotifySuccess?.('Подпись успешно прикреплена')
    } catch (error) {
      onNotifyError(`Ошибка при загрузке подписи: ${(error as any).message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handleCancel = () => {
    resetForm()
    onClose()
  }

  if (!selectedSignature) return null

  const canAttach = !!form.sigBase64 && form.hashMatch

  return (
    <Dialog
      title="Прикрепление подписи для аутсорсера"
      isOpen={isOpen}
      onClose={handleCancel}
      backdropClassName="standard"
      className={`${styles.smallDialog} ${styles.attachDialog}`}
    >
      <div className={Classes.DIALOG_BODY}>
        <div className={styles.infoBox}>
          <GeneralIcon
            type="general-info"
            style={{
              fontSize: "20px",
              flexShrink: 0
            }}
          />
          <div>
            Файл подписи аутсорсера <strong>{selectedSignerName}</strong> будет добавлен к уже имеющимся подписям этого файла — подписи сотрудников сохраняются.<br />
            Файл результата от аутсорсера нужен только для сверки: он должен совпадать с файлом в базе.
          </div>
        </div>

        <div className={styles.attachInfo}>
          <div className={styles.signRow}>
            <span className={styles.label}>Договор:</span>
            <span className={styles.value}>
              {selectedSignature.orderName}
            </span>
          </div>
          <div className={styles.signRow}>
            <span className={styles.label}>Раздел:</span>
            <span className={styles.value}>{selectedSignature.contentName}</span>
          </div>
          <div className={styles.signRow}>
            <span className={styles.label}>Файл в базе:</span>
            <span className={styles.value}>
              <FileLink
                baseUrl={baseUrl}
                file={{
                  fileId: selectedSignature.fileId,
                  fileName: selectedSignature.fileName
                }}
                onRemove={eat}
                readOnly
                style={{
                  whiteSpace: "normal",
                  fontSize: "10px",
                  height: "auto",
                  padding: "8px 12px",
                  borderColor: "#ea561e",
                  color: "#ea561e"
                }}
              />
              <span className={styles.version}> (версия {selectedSignature.version})</span>
            </span>
          </div>
          <div className={styles.signRow}>
            <span className={styles.label}>Роль:</span>
            <span className={styles.value}>{selectedSignature.signerTypeDisplay}</span>
          </div>
        </div>

        <AttachSignatureForm
          key={formKey}
          selectedSignature={selectedSignature}
          baseUrl={baseUrl}
          onNotifyError={onNotifyError}
          onNotifySuccess={onNotifySuccess}
          onChange={handleFormChange}
        />
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <Buttons
          left={
            <div className={styles.dialogButtons}>
              <PlanrButton
                size="small"
                type="greenish"
                onClick={handleAttach}
                disabled={!canAttach || isUploading}
              >
                {isUploading ? 'Загрузка...' : 'Прикрепить подпись'}
              </PlanrButton>
              <PlanrButton
                size="small"
                type="graybtn"
                onClick={handleCancel}
                disabled={isUploading}
              >
                Отмена
              </PlanrButton>
            </div>
          }
        />
      </div>
    </Dialog>
  )
})
